import PropTypes from 'prop-types'
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { FormInputCurrency } from '../../01-Atoms/FormInputCurrency/FormInputCurrency'
import { Section } from '../../02-Molecules/Section/Section'
import './SectionAmount.scss'


/**
 * SectionAmount component.
 */
export const SectionAmount = forwardRef(({ canEdit, collapsed, description, heading, headingLarge, includeMaxLimit, minDonationSingle, minDonationRegular, maxDonationRegular, sectionId, tracking, trackEvent, validationHash, values, donationFeeRate = 1, ...props }, ref) => {
  const [editMode, editModeSet] = useState(isNaN(parseFloat(values.payment_amount)))
  const [hasInitialised, hasInitialisedSet] = useState(false)
  const sectionRef = useRef()

  const minimumDonationSingle = minDonationSingle ? minDonationSingle.toFixed(2) : Number(wgFlowConfiguration['minDonationSingle']).toFixed(2)
  const minimumDonationRegular = minDonationRegular ? minDonationRegular.toFixed(2) : Number(wgFlowConfiguration['minDonationRegular']).toFixed(2)
  const maximumDonationRegular = maxDonationRegular ? maxDonationRegular.toFixed(2) : Number(wgFlowConfiguration['maxDonationRegular']).toFixed(2)

  const isValid = () => {

    if (!isNaN(parseFloat(values.payment_amount))) {
      if (values.payment_type === 'recurring' && parseFloat(values.payment_amount) < minimumDonationRegular) {
        return false
      }
      else if (values.payment_type === 'single' && parseFloat(values.payment_amount) < minimumDonationSingle) {
        return false
      }
      else if (includeMaxLimit && parseFloat(values.payment_amount) > maximumDonationRegular) {
        return false
      }
      else {
        return true
      }
    }

    return false
  }

  useImperativeHandle(ref, () => ({ isValid: () => isValid() }))

  /**
   * Handle any form changes.
   *
   * @param {string} name
   *   The value name
   *
   * @param {string} value
   *   The donation value,
   */
  const handlerInputChange = (name, value) => {
    props.onInputChange(name, value)
  }

  useEffect(() => {
    if (hasInitialised && editMode && sectionRef.current) {
      const input = sectionRef.current.querySelector('input')

      if (input) {
        input.focus()
      }
    }
  }, [editMode])

  useEffect(() => {
    hasInitialisedSet(true)
  }, [])

  /**
   * Set the recap / description text.
   *
   * @type {string}
   *   The generated HTML string.
   */
  const recap = useMemo(() => {
    const text = `<p>${description ? description :
    'You are making a <strong>{DONATION_TYPE}</strong> donation of <strong>£{DONATION_AMOUNT}</strong>'}</p>`
    return text.replace('{DONATION_TYPE}', values.payment_type === 'recurring' ? 'monthly' : values.payment_type).replace('{DONATION_AMOUNT}', values.payment_amount)
  }, [values.payment_amount])


  /**
   * Create the validation criteria array.
   *
   * @type {[{min: number, type: string, message: string}]}
   */
  const validationMessages = useMemo(() => {

    const validationMessages = []

    if (values.payment_type === 'single') {
      validationMessages.push({ type: 'rangeUnderflow', message: `The minimum donation amount is £${minimumDonationSingle}`, min: minimumDonationSingle }
      )
    } else {
      validationMessages.push({ type: 'rangeUnderflow', message: `The minimum monthly donation amount is £${minimumDonationRegular}`, min: minimumDonationRegular })
    }

    if (includeMaxLimit) {
      validationMessages.push({
        type: 'rangeOverflow',
        message: 'Please call our Supporter Relations team if you would like to make a large donation on 0300 303 9876', max: maximumDonationRegular
      })
    }

    return validationMessages
  }, [includeMaxLimit])


  return (
    <Section collapsed={collapsed} heading={heading} headingLarge={headingLarge} sectionId={sectionId} tracking={tracking} trackEvent={trackEvent}>
      {editMode ?
        <div ref={sectionRef}>
          <FormInputCurrency
            label={'Donation amount'}
            value={values.payment_amount}
            required={true}
            name={'payment_amount'}
            type={'number'}
            step={'any'}
            onInputChange={handlerInputChange}
            validationHash={validationHash}
            validationMessages={validationMessages}
          />
        </div> :
        <div className={'o-section-amount__recap'}>
          <div dangerouslySetInnerHTML={{ __html: recap }} />
          {values.uuid || !canEdit ? null : <button className={'b-button-text-blue'} onClick={() => editModeSet(true)}>Change amount</button>}
        </div>
      }
    </Section>
  )
})

/**
 * SectionAmount PropTypes.
 * */
SectionAmount.propTypes = {
  collapsed: PropTypes.bool,
  canEdit: PropTypes.bool,
  includeMaxLimit: PropTypes.bool,
  maxDonationRegular: PropTypes.number,
  minDonationRegular: PropTypes.number,
  minDonationSingle: PropTypes.number,
  heading: PropTypes.string,
  headingLarge: PropTypes.bool,
  sectionId: PropTypes.string,
  description: PropTypes.string,
  onInputChange: PropTypes.func,
  tracking: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
    type: PropTypes.oneOf(['entry', 'exit', 'entry_exit'])
  }),
  trackEvent: PropTypes.func,
  validationHash: PropTypes.string,
  values: PropTypes.shape({
    payment_amount: PropTypes.string,
    payment_type: PropTypes.string,
    uuid: PropTypes.string,
  })
}

SectionAmount.defaultProps = {
  canEdit: true,
  includeMaxLimit: false
}
